import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Minus, Plus, RotateCcw, ShieldCheck, Trash2, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCartStore } from '@/store';
import { shopifyFetch } from '@/shopify';
import { toast } from 'sonner';

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

const CartPage = () => {
  const { items, updateQuantity, removeItem, clearCart } = useCartStore();
  const [checkingOut, setCheckingOut] = useState(false);

  const subtotal = items.reduce((total, item) => total + item.price * item.quantity, 0);
  const shipping = subtotal >= 150 ? 0 : 15;
  const total = subtotal + shipping;
  const freeShippingRemaining = Math.max(0, 150 - subtotal);
  const canCheckout = items.every((item) => item.variantId);

  const handleCheckout = async () => {
    if (checkingOut) return;

    try {
      if (!items.length) {
        toast.error('Seu carrinho está vazio');
        return;
      }

      if (!canCheckout) {
        toast.error('Há um item indisponível no carrinho. Remova-o para finalizar a compra.');
        return;
      }

      setCheckingOut(true);

      const lines = items.map((item) => ({
        merchandiseId: item.variantId,
        quantity: item.quantity
      }));

      const data = await shopifyFetch(
        `
        mutation CreateCart($lines: [CartLineInput!]!) {
          cartCreate(input: { lines: $lines }) {
            cart {
              id
              checkoutUrl
            }
            userErrors {
              field
              message
            }
          }
        }
        `,
        { lines }
      );

      const userErrors = data?.cartCreate?.userErrors || [];

      if (userErrors.length > 0) {
        void userErrors;
        toast.error(userErrors[0].message || 'Erro ao criar checkout');
        setCheckingOut(false);
        return;
      }

      const checkoutUrl = data?.cartCreate?.cart?.checkoutUrl;

      if (!checkoutUrl) {
        toast.error('Não foi possível gerar o checkout');
        setCheckingOut(false);
        return;
      }

      const returnTo = `${window.location.origin}/pedido-confirmado`;
      const finalUrl = `${checkoutUrl}${checkoutUrl.includes('?') ? '&' : '?'}return_to=${encodeURIComponent(returnTo)}`;

      window.location.href = finalUrl;
    } catch (error) {
      void error;
      toast.error('Erro ao ir para o checkout');
      setCheckingOut(false);
    }
  };

  if (!items.length) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-6" data-testid="empty-cart-page">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-[1400px] mx-auto text-center py-20"
        >
          <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6 text-text-primary">
            Seu Carrinho está Vazio
          </h1>
          <p className="text-lg text-text-secondary mb-8">
            Adicione alguns produtos e volte aqui para finalizar sua compra
          </p>
          <Link to="/produtos">
            <Button className="bg-accent text-white hover:bg-accent/90 rounded-full px-10 py-6 text-lg">
              Continuar Comprando
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-6" data-testid="cart-page">
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="font-heading text-4xl md:text-6xl font-bold mb-4 text-text-primary">
            Carrinho de Compras
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-text-secondary">
            <div className="flex items-center gap-2 rounded-lg border border-accent/10 bg-white px-4 py-3">
              <Truck className="w-4 h-4 text-accent" />
              <span>Frete grátis acima de R$ 150</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-accent/10 bg-white px-4 py-3">
              <ShieldCheck className="w-4 h-4 text-accent" />
              <span>Checkout seguro pela Shopify</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-accent/10 bg-white px-4 py-3">
              <RotateCcw className="w-4 h-4 text-accent" />
              <span>Suporte para trocas e dúvidas</span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            <AnimatePresence initial={false}>
              {items.map((item, idx) => (
                <motion.div
                  key={`${item.id}-${idx}`}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  className="bg-white rounded-2xl p-6 shadow-sm flex flex-col md:flex-row gap-6 items-center"
                  data-testid={`cart-item-${idx}`}
                >
                <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden shrink-0">
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1 w-full">
                  <h2 className="text-2xl font-body text-text-primary mb-2">
                    {item.name}
                  </h2>
                  <p className="text-text-muted mb-4">
                    {item.category || 'Sem categoria'}
                  </p>

                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      aria-label={`Diminuir quantidade de ${item.name}`}
                      data-testid={`decrease-quantity-${idx}`}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>

                    <span className="text-lg min-w-[24px] text-center">
                      {item.quantity}
                    </span>

                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      aria-label={`Aumentar quantidade de ${item.name}`}
                      data-testid={`increase-quantity-${idx}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="text-right flex flex-col items-end gap-4">
                  <div>
                    <p className="font-heading text-3xl font-bold text-accent">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                    <p className="text-text-muted">
                      {formatCurrency(item.price)} cada
                    </p>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(item.id)}
                    aria-label={`Remover ${item.name} do carrinho`}
                    data-testid={`remove-item-${idx}`}
                  >
                    <Trash2 className="w-5 h-5 text-accent" />
                  </Button>
                </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="bg-white rounded-2xl p-8 shadow-sm h-fit lg:sticky lg:top-24"
            data-testid="order-summary"
          >
            <h2 className="font-heading text-4xl font-bold mb-8 text-text-primary">
              Resumo do Pedido
            </h2>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-lg">
                <span className="text-text-secondary">Subtotal</span>
                <span className="text-text-primary">{formatCurrency(subtotal)}</span>
              </div>

              <div className="flex justify-between text-lg">
                <span className="text-text-secondary">Frete</span>
                <span className="text-text-primary">
                  {shipping === 0 ? 'Grátis' : formatCurrency(shipping)}
                </span>
              </div>
            </div>

            <div className="border-t pt-6 mb-6">
              <div className="flex justify-between items-center">
                <span className="font-heading text-3xl font-bold text-text-primary">Total</span>
                <span className="font-heading text-4xl font-bold text-accent">
                  {formatCurrency(total)}
                </span>
              </div>
            </div>

            {freeShippingRemaining > 0 ? (
              <div className="mb-6 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5 text-text-secondary">
                    <Truck className="w-4 h-4 text-accent" />
                    <span>Frete grátis acima de R$ 150</span>
                  </div>
                  <span className="text-accent font-medium">
                    Faltam {formatCurrency(freeShippingRemaining)}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-accent h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, (subtotal / 150) * 100)}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="mb-6 flex items-center gap-2 text-accent font-medium text-sm bg-accent/5 rounded-lg p-3">
                <Truck className="w-4 h-4" />
                <span>Parabéns! Você ganhou frete grátis.</span>
              </div>
            )}

            <div className="space-y-4">
              <Button
                onClick={handleCheckout}
                disabled={checkingOut || !canCheckout}
                className="w-full bg-accent text-white hover:bg-accent/90 rounded-full py-6 text-xl"
                data-testid="checkout-button"
              >
                {checkingOut ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Abrindo checkout...
                  </>
                ) : (
                  'Finalizar Compra'
                )}
              </Button>

              <Link to="/produtos">
                <Button
                  variant="outline"
                  className="w-full rounded-full py-6 text-lg"
                  data-testid="continue-shopping-button"
                >
                  Continuar Comprando
                </Button>
              </Link>

              <Button
                variant="ghost"
                className="w-full text-text-muted"
                onClick={clearCart}
                disabled={checkingOut}
                data-testid="clear-cart-button"
              >
                Limpar carrinho
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
